/**
 * Sheet `duedate-sheet` — Anlegen **und** Ändern in einer Komponente,
 * dieselbe Aufteilung wie `task-sheet` (`features/tasks/TaskSheet.tsx`,
 * Spec 06b, „Ein Sheet für beide Modi, ein Bezeichner"). `dueDate === null`
 * heißt Anlegen.
 *
 * Die Vorlagenauswahl (`duedate-template`) füllt Bezeichnung, Wiederholung
 * und Vorlauf vor (Spec, „Vorlagenauswahl") — reine Vorbelegung, kein
 * eigener Schreibvorgang: Erst `duedate-save` legt an oder ändert (AK-410).
 *
 * Der Hinweisstreifen `duedate-template-note` benutzt **nicht**
 * `components/ConfirmationStrip.tsx`: Jene Komponente ist ein
 * `fixed`-positioniertes, sich nach 4s selbst schließendes Overlay für
 * Bestätigungen *nach* einer Aktion (Spec 04b/05a) — hier wird dagegen ein
 * ruhiger Hinweis **innerhalb** des Formulars gebraucht, der stehen bleibt,
 * solange eine Vorlage gewählt ist, auch während der Nutzer andere Felder
 * ändert (AK-410: „eine Änderung entfernt den Hinweis nicht"). „Bauform
 * ConfirmationStrip" (Spec 06b) ist deshalb als dieselbe ruhige, geteilte
 * Optik gelesen (Rahmen, gedämpfter Text), nicht als Import derselben,
 * unpassenden Komponente.
 *
 * Das Objekt (`duedate-subject`) steht als Text im Kopf, nicht wählbar — das
 * gilt weiterhin am Objekt (06b). Seit Spec 07 hat das Sheet einen zweiten
 * Einstieg **ohne** festes Objekt: Fehlen `subjectId`/`subjectLabel` (nur
 * beim Anlegen sinnvoll), ersetzt `duedate-subject-picker` (`EntityPicker`)
 * den Objekttext — das erste, fokussierte Feld (ADR 0017 Punkt 9). Dasselbe
 * Sheet, zwei Einstiege, keine zweite Komponente.
 */

import { useEffect, useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { AssigneeField } from '@/components/AssigneeField'
import { EntityPicker } from '@/components/EntityPicker'
import type { EntitySelection } from '@/components/EntityPicker'
import { FIELD_CLASS, FormField, FormTextareaField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import { ApiError } from '@/lib/api'
import { useCreateDueDate, useDueDateRecurrences, useDueDateTemplates, useUpdateDueDate } from '@/features/schedule/api'
import type { DueDateRow, DueDateTemplate } from '@/features/schedule/api'
import {
  RECURRENCE_MONTHS_REQUIRED_MESSAGE,
  TASK_ASSIGNEE_HINT,
  TASK_ASSIGNEE_LABEL,
  buildDueDateCreateBody,
  buildDueDatePatchBody,
  deriveEditForm,
  hasInvalidRecurrenceMonths,
  templateFormPatch,
  templateNoteText,
} from '@/features/schedule/duedate-form'
import type { DueDateFormValues } from '@/features/schedule/duedate-form'

interface DueDateSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  /** Fest vorgegeben am Objekt (06b, Reiter *Termine*) — der Objekttext bleibt
   * unwählbar. Fehlt sie, zeigt das Sheet stattdessen `duedate-subject-picker`
   * (Spec 07, Kalender) — nur beim Anlegen sinnvoll (`dueDate === null`). */
  readonly subjectId?: string
  readonly subjectLabel?: string
  /** `null` für Anlegen, sonst der zu ändernde Termin. */
  readonly dueDate: DueDateRow | null
  /** Vorbelegtes Fälligkeitsdatum beim Anlegen aus dem Kalender
   * (`kal-tag-add-<datum>` bzw. `duedate-add`, AK-461) — ignoriert beim
   * Ändern. */
  readonly initialDueOn?: string
}

const EMPTY_FORM: DueDateFormValues = {
  title: '',
  dueOn: '',
  recurrenceOption: 'none',
  recurrenceMonthsCustom: '',
  leadDays: '30',
  taskAssigneeId: '',
  notes: '',
}

export function DueDateSheet({
  open,
  onOpenChange,
  subjectId,
  subjectLabel,
  dueDate,
  initialDueOn,
}: DueDateSheetProps) {
  const isEdit = dueDate !== null
  // Kein festes Objekt mitgegeben → `duedate-subject-picker` statt des
  // unwählbaren Texts (Spec 07). Am Objekt (06b) ist `subjectId` immer
  // gesetzt, dieser Zweig bleibt dort also unverändert `false`.
  const hasFixedSubject = subjectId !== undefined
  const createMutation = useCreateDueDate()
  const updateMutation = useUpdateDueDate()
  const templatesQuery = useDueDateTemplates()
  const recurrencesQuery = useDueDateRecurrences()
  const templateSelectRef = useRef<HTMLSelectElement>(null)
  const subjectPickerRef = useRef<HTMLDivElement>(null)

  const [form, setForm] = useState<DueDateFormValues>(EMPTY_FORM)
  const [initialForm, setInitialForm] = useState<DueDateFormValues>(EMPTY_FORM)
  const [selectedTemplateKey, setSelectedTemplateKey] = useState('')
  const [isMoreOpen, setIsMoreOpen] = useState(false)
  const [pickedSubject, setPickedSubject] = useState<EntitySelection | null>(null)
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)

  // Jedes Öffnen beginnt frisch — dieselbe Bauform wie `TaskSheet`
  // („Adjusting state when a prop changes", während des Renderns statt in
  // einem Effekt).
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      const nextForm = dueDate !== null ? deriveEditForm(dueDate) : { ...EMPTY_FORM, dueOn: initialDueOn ?? EMPTY_FORM.dueOn }
      setForm(nextForm)
      setInitialForm(nextForm)
      setSelectedTemplateKey(dueDate?.template_key ?? '')
      setIsMoreOpen(dueDate !== null && dueDate.task_assignee !== null)
      setPickedSubject(null)
      setFieldError(undefined)
      setGeneralError(undefined)
    }
  }

  function updateField(name: keyof DueDateFormValues) {
    return (value: string) => {
      setForm((current) => ({ ...current, [name]: value }))
    }
  }

  function resetForAnother() {
    setForm({ ...EMPTY_FORM, dueOn: initialDueOn ?? EMPTY_FORM.dueOn })
    setInitialForm({ ...EMPTY_FORM, dueOn: initialDueOn ?? EMPTY_FORM.dueOn })
    setSelectedTemplateKey('')
    setIsMoreOpen(false)
    setPickedSubject(null)
    setFieldError(undefined)
    setGeneralError(undefined)
    if (hasFixedSubject) {
      templateSelectRef.current?.focus()
    } else {
      subjectPickerRef.current?.focus()
    }
  }

  function applyTemplate(template: DueDateTemplate) {
    setForm((current) => ({ ...current, ...templateFormPatch(template) }))
  }

  const isDirty = JSON.stringify(form) !== JSON.stringify(initialForm)
  const canSubmit = isEdit
    ? isDirty
    : form.title.trim() !== '' && form.dueOn !== '' && (hasFixedSubject || pickedSubject !== null)
  const isPending = createMutation.isPending || updateMutation.isPending
  const selectedTemplate = templatesQuery.data?.find((template) => template.key === selectedTemplateKey) ?? null

  async function submit(andAnother: boolean) {
    if (!canSubmit || isPending) return
    setFieldError(undefined)
    setGeneralError(undefined)

    // K5: „anderes Intervall" ohne gültige Zahl ist am Server ein gültiges
    // `null` (= einmalig) — kein 422. Vor dem Senden geprüft, damit eine
    // gewählte Wiederholung nie still verloren geht.
    if (hasInvalidRecurrenceMonths(form)) {
      setFieldError({ field: 'recurrence_months', message: RECURRENCE_MONTHS_REQUIRED_MESSAGE })
      return
    }

    try {
      if (isEdit) {
        const patchBody = buildDueDatePatchBody(form, initialForm)
        if (Object.keys(patchBody).length > 0) {
          await updateMutation.mutateAsync({
            dueDateId: dueDate.id,
            ifMatch: String(dueDate.version),
            body: patchBody,
          })
        }
        onOpenChange(false)
        return
      }

      // `canSubmit` hat bereits geprüft, dass ein Objekt feststeht — fest
      // vorgegeben oder aus dem Picker gewählt. `??` statt `hasFixedSubject
      // ? subjectId : …`: TypeScript engt `subjectId` über eine separate
      // Bedingungsvariable nicht auf `string` ein, über die Verkettung schon.
      const resolvedSubjectId = subjectId ?? pickedSubject?.id ?? ''
      await createMutation.mutateAsync({
        body: buildDueDateCreateBody(resolvedSubjectId, selectedTemplateKey === '' ? null : selectedTemplateKey, form),
      })
      if (andAnother) {
        resetForAnother()
      } else {
        onOpenChange(false)
      }
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    await submit(false)
  }

  // Dieselbe Ref-Bauform wie `TaskSheet` — `submit` schließt über `form` und
  // ändert sich bei jedem Rendern; der Tastenlistener unten liest die
  // jeweils aktuelle Fassung, ohne bei jeder Eingabe neu an- und abgemeldet
  // zu werden.
  const submitRef = useRef(submit)
  useEffect(() => {
    submitRef.current = submit
  })

  useEffect(() => {
    if (!open) return
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault()
        void submitRef.current(false)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const headSubject = isEdit ? dueDate.subject.label : subjectLabel
  const assigneeSubjectId = isEdit ? dueDate.subject.id : (subjectId ?? pickedSubject?.id ?? '')

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          if (!isEdit && !hasFixedSubject) {
            subjectPickerRef.current?.focus()
          } else {
            templateSelectRef.current?.focus()
          }
        }}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[480px] md:data-[side=right]:!max-w-[480px]"
      >
        <form
          data-testid="duedate-sheet"
          onSubmit={(event) => void handleSubmit(event)}
          className="flex h-full flex-col"
        >
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">
              {isEdit ? 'Termin ändern' : 'Termin anlegen'}
            </SheetTitle>
            {isEdit || hasFixedSubject ? (
              <p data-testid="duedate-subject" className="text-label text-muted">
                {headSubject}
              </p>
            ) : (
              // Kein Objekt vorgegeben (Kalender, Spec 07 AK-461) — der
              // Picker ersetzt den Text an derselben Stelle und ist damit das
              // erste, fokussierte Feld des Sheets (ADR 0017 Punkt 9).
              <EntityPicker
                ref={subjectPickerRef}
                testId="duedate-subject-picker"
                label="Objekt"
                value={pickedSubject}
                onSelect={setPickedSubject}
                error={fieldError}
                errorPrefix="duedate"
                apiField="subject_id"
              />
            )}
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <label className="text-label text-muted flex flex-col gap-1" htmlFor="duedate-template">
              Vorlage
              <select
                id="duedate-template"
                ref={templateSelectRef}
                data-testid="duedate-template"
                disabled={templatesQuery.isPending}
                value={selectedTemplateKey}
                onChange={(event) => {
                  const key = event.target.value
                  setSelectedTemplateKey(key)
                  const template = templatesQuery.data?.find((entry) => entry.key === key)
                  if (template !== undefined) applyTemplate(template)
                }}
                className={`text-text ${FIELD_CLASS}`}
              >
                <option value="" data-testid="duedate-template-none">
                  Eigener Termin
                </option>
                {(templatesQuery.data ?? []).map((template) => (
                  <option key={template.key} value={template.key}>
                    {template.title}
                  </option>
                ))}
              </select>
            </label>

            {selectedTemplate !== null && (
              // W25: `bg-bg` (#FCFCFD) gegen `bg-surface` (#FFFFFF) der Felder
              // war auf der Aufnahme kaum zu unterscheiden, `text-muted` traf
              // dieselbe Farbe wie das Feldlabel — der einzige sichtbare
              // Träger von `FACHREGELN.md` Regel 2 sah aus wie ein leeres
              // Feld. `bg-border` ist der Token, den der Skill für
              // Flächenkontrast ohne Schatten vorsieht („Struktur":
              // „Abgrenzung über 1px-Border und Flächenkontrast"); ein
              // eigener Rahmen entfiele damit (gleiche Farbe wie die Fläche).
              <p
                data-testid="duedate-template-note"
                role="status"
                className="bg-border text-label text-text rounded-card px-3 py-2"
              >
                {templateNoteText(selectedTemplate.note)}
              </p>
            )}

            <FormField
              label="Bezeichnung"
              testId="duedate-title"
              apiField="title"
              errorPrefix="duedate"
              required
              value={form.title}
              onChange={updateField('title')}
              error={fieldError}
            />

            <FormField
              label="Fällig am"
              testId="duedate-due-on"
              apiField="due_on"
              errorPrefix="duedate"
              type="date"
              required
              value={form.dueOn}
              onChange={updateField('dueOn')}
              error={fieldError}
            />

            <label className="text-label text-muted flex flex-col gap-1" htmlFor="duedate-recurrence">
              Wiederholung
              <select
                id="duedate-recurrence"
                data-testid="duedate-recurrence"
                disabled={recurrencesQuery.isPending}
                value={form.recurrenceOption}
                onChange={(event) => {
                  updateField('recurrenceOption')(event.target.value)
                }}
                className={`text-text ${FIELD_CLASS}`}
              >
                {(recurrencesQuery.data ?? []).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {form.recurrenceOption === 'custom' && (
              <FormField
                label="Alle … Monate"
                testId="duedate-recurrence-months"
                apiField="recurrence_months"
                errorPrefix="duedate"
                inputMode="numeric"
                value={form.recurrenceMonthsCustom}
                onChange={updateField('recurrenceMonthsCustom')}
                error={fieldError}
              />
            )}

            <FormTextareaField
              label="Notizen"
              testId="duedate-notes"
              apiField="notes"
              errorPrefix="duedate"
              value={form.notes}
              onChange={updateField('notes')}
              error={fieldError}
            />

            <details
              open={isMoreOpen}
              onToggle={(event) => {
                setIsMoreOpen(event.currentTarget.open)
              }}
            >
              <summary
                data-testid="duedate-more"
                className="text-label text-muted min-h-(--tap-min) cursor-pointer list-item py-2"
              >
                Weitere Angaben
              </summary>

              <div className="flex flex-col gap-4 pt-2">
                <div className="flex flex-col gap-1">
                  <FormField
                    label="Vorlauf in Tagen"
                    testId="duedate-lead-days"
                    apiField="lead_days"
                    errorPrefix="duedate"
                    inputMode="numeric"
                    value={form.leadDays}
                    onChange={updateField('leadDays')}
                    error={fieldError}
                  />
                  <p className="text-label text-muted">So früh gilt der Termin als „Steht an".</p>
                </div>

                <div className="flex flex-col gap-1">
                  <AssigneeField
                    subjectId={assigneeSubjectId}
                    testId="duedate-task-assignee"
                    label={TASK_ASSIGNEE_LABEL}
                    apiField="task_assignee_id"
                    errorPrefix="duedate"
                    value={form.taskAssigneeId}
                    onChange={updateField('taskAssigneeId')}
                    error={fieldError}
                  />
                  <p className="text-label text-muted">{TASK_ASSIGNEE_HINT}</p>
                </div>
              </div>
            </details>
          </div>

          <SheetFooter>
            {generalError !== undefined && (
              <p data-testid="duedate-error" role="alert" className="text-label text-status-overdue">
                {generalError}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="duedate-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              {!isEdit && (
                <button
                  type="button"
                  data-testid="duedate-save-and-next"
                  disabled={!canSubmit || isPending}
                  aria-busy={isPending}
                  onClick={() => void submit(true)}
                  className={`text-text px-4 ${FIELD_CLASS} disabled:opacity-60`}
                >
                  Anlegen und noch eins
                </button>
              )}
              <button
                type="submit"
                data-testid="duedate-save"
                disabled={!canSubmit || isPending}
                aria-busy={isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                {isEdit ? 'Speichern' : 'Anlegen'}
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
